import { useEffect, useState } from 'react';
import { EndedUnit, getEndedUnits } from '../../../services/adminUnits';
import { failureReason } from '../adminErrors';
import GraphToggle from './GraphToggle';
import UnitUsageGraph from './UnitUsageGraph';
import { exitReasonLabel, formatMoment, plural, spanUntil } from './unitsFormat';

interface EndedUnitsProps {
  imageTag: string;
  cpuCores: number;
  memoryGb: number;
}

function EndedUnitRow({ unit, cpuCores, memoryGb }: { unit: EndedUnit; cpuCores: number; memoryGb: number }) {
  const [graph, setGraph] = useState(false);
  return (
    <div role="listitem" aria-label={unit.unitId} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 0', borderTop: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
        <span className="mono">{unit.unitId}</span>
        <span className="muted">
          {spanUntil(unit.startedAt, unit.endedAt)} · {plural(unit.jobsDone, 'job', 'jobs')} · {exitReasonLabel(unit.exitReason)} · ended{' '}
          {formatMoment(unit.endedAt)}
        </span>
        <GraphToggle shown={graph} onToggle={() => setGraph(!graph)} />
      </div>
      {graph && <UnitUsageGraph unitId={unit.unitId} cpuCores={cpuCores} memoryGb={memoryGb} />}
    </div>
  );
}

/** The units of one image and size that ended in the last 7 days, each with its graph on demand. */
export default function EndedUnits({ imageTag, cpuCores, memoryGb }: EndedUnitsProps) {
  const [units, setUnits] = useState<EndedUnit[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    getEndedUnits(imageTag, cpuCores, memoryGb)
      .then((read) => current && setUnits(read))
      .catch((err) => current && setError(failureReason(err)));
    return () => {
      current = false;
    };
  }, [imageTag, cpuCores, memoryGb]);

  if (error) return <div className="field"><div className="err">Could not load the units: {error}</div></div>;
  if (!units) return <div className="muted" style={{ fontSize: 12.5 }}>Loading…</div>;
  return (
    <div role="list" aria-label={`Units of ${imageTag}`}>
      {units.map((unit) => (
        <EndedUnitRow key={unit.unitId} unit={unit} cpuCores={cpuCores} memoryGb={memoryGb} />
      ))}
    </div>
  );
}
